import React, { useMemo } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle } from 'lucide-react';

/**
 * Step 3 wizard — Definizione economica + dati commerciali (F.3.2).
 *
 * Modalità mutuamente esclusive:
 *   A) agreed_total — Prezzo pattuito complessivo
 *   B) discount_percent — Ribasso percentuale
 *
 * Il backend resta autoritativo per il calcolo. Il frontend mostra solo
 * una preview client-side (a scopo UX) e usa il totale server-side dopo POST/PUT.
 *
 * Props:
 * - baseInternalAmount: number (valore base interno, valorizzato solo per utenti autorizzati)
 * - showInternalPreview: boolean
 * - pricingMode, agreedTotalAmount, discountPercent, validUntil
 * - notes, exclusions, conditions (F.3.2 — dati commerciali persistiti)
 * - onChange: (patch) => void
 */
export const SubcontractOfferPricingStep = ({
  baseInternalAmount,
  showInternalPreview = false,
  pricingMode,
  agreedTotalAmount,
  discountPercent,
  validUntil,
  notes,
  exclusions,
  conditions,
  onChange,
  testIdPrefix = 'pricing',
}) => {
  const preview = useMemo(() => {
    // Preview solo lato UI. Backend è autoritativo dopo il salvataggio.
    if (!pricingMode) return null;
    const base = Number(baseInternalAmount) || 0;
    if (pricingMode === 'agreed_total') {
      const aa = Number(agreedTotalAmount);
      if (!agreedTotalAmount || isNaN(aa)) return null;
      if (aa < 0) return { invalid: 'Importo negativo non ammesso' };
      return { agreedTotal: aa, delta: base - aa };
    }
    if (pricingMode === 'discount_percent') {
      const dp = Number(discountPercent);
      if (!discountPercent || isNaN(dp)) return null;
      if (dp < 0 || dp >= 100) return { invalid: 'Ribasso deve essere in [0, 100)' };
      const aa = base * (100 - dp) / 100;
      return { agreedTotal: aa, discountPercent: dp, delta: base - aa };
    }
    return null;
  }, [pricingMode, agreedTotalAmount, discountPercent, baseInternalAmount]);

  const selectMode = (mode) => {
    if (mode === pricingMode) return;
    // Reset campi dell'altra modalità (mutua esclusività)
    if (mode === 'agreed_total') {
      onChange({ pricing_mode: 'agreed_total', discount_percent: null });
    } else {
      onChange({ pricing_mode: 'discount_percent', agreed_total_amount: null });
    }
  };

  return (
    <div className="space-y-6" data-testid="pricing-step">

      {/* Marker modalità corrente (per test edit) */}
      <div style={{ display: 'none' }} data-testid={`${testIdPrefix}-pricing-mode`}>{pricingMode || ''}</div>

      <div>
        <div className="text-sm font-medium mb-2">Modalità economica</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => selectMode('agreed_total')}
            className={`p-4 rounded-lg border-2 text-left transition ${pricingMode === 'agreed_total' ? 'border-[#FF7819] bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
            data-testid="pricing-mode-agreed-total"
          >
            <div className="font-medium">Prezzo pattuito complessivo</div>
            <div className="text-xs text-slate-600 mt-1">Importo forfettario per l&apos;intero pacchetto di lavorazioni.</div>
          </button>
          <button
            type="button"
            onClick={() => selectMode('discount_percent')}
            className={`p-4 rounded-lg border-2 text-left transition ${pricingMode === 'discount_percent' ? 'border-[#FF7819] bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
            data-testid="pricing-mode-discount-percent"
          >
            <div className="font-medium">Ribasso percentuale</div>
            <div className="text-xs text-slate-600 mt-1">Ribasso applicato al valore base interno delle lavorazioni.</div>
          </button>
        </div>
      </div>

      {pricingMode === 'agreed_total' && (
        <div className="space-y-2">
          <Label htmlFor="agreed_total_amount">Importo complessivo pattuito (€)</Label>
          <Input
            id="agreed_total_amount"
            type="number"
            step="0.01"
            min="0"
            value={agreedTotalAmount ?? ''}
            onChange={(e) => onChange({ agreed_total_amount: e.target.value })}
            placeholder="0.00"
            data-testid={testIdPrefix === 'pricing' ? 'pricing-agreed-total-input' : 'subcontract-offer-edit-agreed-total'}
          />
        </div>
      )}

      {pricingMode === 'discount_percent' && (
        <div className="space-y-2">
          <Label htmlFor="discount_percent">Ribasso percentuale (%)</Label>
          <Input
            id="discount_percent"
            type="number"
            step="0.01"
            min="0"
            max="99.99"
            value={discountPercent ?? ''}
            onChange={(e) => onChange({ discount_percent: e.target.value })}
            placeholder="0.00"
            data-testid={testIdPrefix === 'pricing' ? 'pricing-discount-percent-input' : 'subcontract-offer-edit-discount-percent'}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="valid_until">Validità fino a</Label>
          <Input
            id="valid_until"
            type="date"
            value={validUntil ?? ''}
            onChange={(e) => onChange({ valid_until: e.target.value })}
            data-testid="pricing-valid-until-input"
          />
        </div>
      </div>

      {/* F.3.2 — dati commerciali persistiti */}
      <div className="space-y-3">
        <div className="text-sm font-medium text-slate-700">Dati commerciali</div>
        <div className="space-y-2">
          <Label htmlFor="notes">Note</Label>
          <Textarea
            id="notes"
            value={notes ?? ''}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="Note che accompagnano l'offerta"
            rows={3}
            data-testid={testIdPrefix === 'pricing' ? 'pricing-notes-input' : 'subcontract-offer-edit-notes'}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="exclusions">Esclusioni</Label>
          <Textarea
            id="exclusions"
            value={exclusions ?? ''}
            onChange={(e) => onChange({ exclusions: e.target.value })}
            placeholder="Cosa NON è incluso nell'offerta"
            rows={3}
            data-testid={testIdPrefix === 'pricing' ? 'pricing-exclusions-input' : 'subcontract-offer-edit-exclusions'}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="conditions">Condizioni</Label>
          <Textarea
            id="conditions"
            value={conditions ?? ''}
            onChange={(e) => onChange({ conditions: e.target.value })}
            placeholder="Termini di pagamento, tempi di consegna, garanzie, ecc."
            rows={3}
            data-testid={testIdPrefix === 'pricing' ? 'pricing-conditions-input' : 'subcontract-offer-edit-conditions'}
          />
        </div>
      </div>

      {preview?.invalid && (
        <Alert variant="destructive" data-testid="pricing-preview-invalid">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{preview.invalid}</AlertDescription>
        </Alert>
      )}

      {preview && !preview.invalid && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="pricing-preview">
          {showInternalPreview && (
            <div className="text-sm">
              <span className="text-slate-600">Valore base interno complessivo: </span>
              <b data-testid="pricing-preview-base">€ {Number(baseInternalAmount || 0).toFixed(2)}</b>
            </div>
          )}
          <div className="text-sm">
            <span className="text-slate-600">Importo finale offerta (anteprima): </span>
            <b data-testid="pricing-preview-agreed">€ {(preview.agreedTotal || 0).toFixed(2)}</b>
          </div>
          {preview.discountPercent !== undefined && (
            <div className="text-sm">
              <span className="text-slate-600">Ribasso: </span>
              <b>{preview.discountPercent.toFixed(2)}%</b>
            </div>
          )}
          {showInternalPreview && (
            <div className="text-sm">
              <span className="text-slate-600">Differenza interna: </span>
              <b data-testid="pricing-preview-delta">€ {(preview.delta || 0).toFixed(2)}</b>
            </div>
          )}
          <div className="text-xs text-slate-500 pt-2">
            Anteprima calcolata lato client. L'importo finale è autoritativamente definito dal backend al salvataggio.
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractOfferPricingStep;
